import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFriedhof } from '../contexts/FriedhofContext';
import { LoginRequest } from '../types';
import { brand } from '../config/brand';
import { Button, Field, Input, Spinner } from '../components/ui';

const Login: React.FC = () => {
  const { login, isAuthenticated, isLoading } = useAuth();
  const { resetFriedhofSelection } = useFriedhof();
  const location = useLocation();
  const [credentials, setCredentials] = useState<LoginRequest>({ username: '', password: '' });
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const from = (location.state as any)?.from?.pathname || '/dashboard';

  if (isAuthenticated) return <Navigate to={from} replace />;

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 text-brand-600">
        <Spinner className="h-12 w-12" />
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      // Force the Friedhof selection screen after every fresh login.
      resetFriedhofSelection();
      await login(credentials);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Anmeldung fehlgeschlagen');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCredentials(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-gradient-to-br from-brand-50 via-gray-50 to-gray-100 px-4 py-12">
      {/* dezente Atmosphäre im Hintergrund */}
      <div className="pointer-events-none absolute -top-24 -right-24 h-96 w-96 rounded-full bg-brand-200/40 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -left-24 h-96 w-96 rounded-full bg-brand-100/50 blur-3xl" />

      <div className="relative w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-2xl bg-brand-600 text-white shadow-lg shadow-brand-600/20">
            <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-gray-900">{brand.appName}</h1>
          <p className="mt-1 text-sm text-gray-500">Bitte melden Sie sich an, um fortzufahren.</p>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white/90 p-8 shadow-xl shadow-gray-900/5 backdrop-blur">
          <form className="space-y-5" onSubmit={handleSubmit}>
            <Field label="Benutzername">
              <Input name="username" type="text" required autoFocus placeholder="Benutzername"
                value={credentials.username} onChange={handleChange} disabled={isSubmitting} />
            </Field>
            <Field label="Passwort">
              <Input name="password" type="password" required placeholder="Passwort"
                value={credentials.password} onChange={handleChange} disabled={isSubmitting} />
            </Field>

            {error && (
              <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                <svg className="mt-0.5 h-5 w-5 shrink-0 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span>{error}</span>
              </div>
            )}

            <Button type="submit" loading={isSubmitting} className="w-full">
              {isSubmitting ? 'Anmeldung läuft…' : 'Anmelden'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
